'use client';

/**
 * @packageDocumentation
 * @category Hook
 */

import { useCallback, useSyncExternalStore } from 'react';

/**
 * Whether the viewport matches a media query, read during render.
 *
 * `useSyncExternalStore` rather than an effect with state, so a screen never
 * paints one layout and then corrects itself. The server has no viewport, so
 * the caller says what the first HTML assumes: a sidebar assumes the sidebar
 * width, a dialog assumes the dialog. Returning the live match there would
 * differ between server and client and React would discard the markup.
 *
 * One definition for the console rail, the grid toolbar and the dialog, which
 * each carried this subscription inline before 2026-09-20.
 */
export const useMediaQuery = (query: string, serverMatches = false): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const read = useCallback(() => window.matchMedia(query).matches, [query]);
  return useSyncExternalStore(subscribe, read, () => serverMatches);
};
